import * as React from "react";
import { useEffect } from "react";

import { Notebook } from "@learningblocks/simulations/shared-components";
import { Utilities } from "@learningblocks/exp-constants";
const { resetScroll } = Utilities;
import Modal from "react-modal";
import { PopoutFooter } from "@learningblocks/simulations/shared-components";
import { ProgressBarBlock } from "@learningblocks/simulations/hotspot-sim-components";
import { BackAndForthSlab } from "@labxchange/ui-components/lib/BackAndForthSlab";

const appElement = document.getElementById("container");
if (appElement) {
  Modal.setAppElement("#container");
}

export type MenuValue =
  | "CONTEXT"
  | "MATERIALS"
  | "PREDICTIONS"
  | "TECHNIQUES"
  | "DESIGN"
  | "PROTOCOL"
  | "PROBLEM_SOLVING"
  | "RESULTS"
  | "REFLECTION"
  | "TROUBLESHOOTING"
  | "SUMMARY"
  | "NONE"
  | undefined;

export interface MenuItem {
  title: string;
  current: boolean;
  number: number;
  value: string;
  visited: boolean;
  displayProgress: boolean;
  maxProgress?: number;
  currentProgress?: number;
  activitiesCount?: number;
}

export interface ComponentItem {
  id: string;
  isComplete: boolean;
  isActivity: boolean;
  isPredictionQuestion?: boolean;
  answerId?: string[];
}

export interface CurrentMenu {
  number: number;
  title: string;
  value: string;
  maxProgress?: number;
  currentProgress?: number;
}

export interface CommonSectionContentProps {
  components?: ComponentItem[];
  currentMenu: CurrentMenu;
  description?: string;
  goToSection: (menuValue: string) => void;
  updateProgress: (params: {
    menu: string;
    menuProgress: number;
    overallProgress: number;
  }) => void;
  menuList: MenuItem[];
  nextMenuValue?: MenuValue;
  prevMenuValue?: MenuValue;
  title?: string;
  /** @deprecated Not used in the component */
  bannerText?: string;
  currentProgress: number;
  collapsed?: boolean;
  simSpecificComponentMap?: Record<string, React.ComponentType<unknown>>;
  displayProgress?: boolean;
  usingNewDesignSystem?: boolean;
  maxProgress?: number;
  progress?: boolean;
}

export function CommonSectionContentComponent({
  components,
  currentMenu,
  description,
  goToSection,
  updateProgress,
  menuList,
  nextMenuValue,
  prevMenuValue,
  title,
  // bannerText parameter removed as it's unused
  currentProgress,
  collapsed,
  simSpecificComponentMap,
  displayProgress,
  usingNewDesignSystem,
  maxProgress,
  progress,
}: CommonSectionContentProps) {
  useEffect(() => {
    resetScroll("book_content_body");
    handleUpdateProgress(currentMenu.value);
  }, [currentMenu.value]);

  const isLastChapter = nextMenuValue === undefined;
  const isFirstChapter = prevMenuValue === undefined;

  const contentBodyClass = isLastChapter
    ? "book_content_body lastChapter"
    : "book_content_body";

  const handleUpdateProgress = (menuValue: string) => {
    const menuValueObject = menuList.find((item) => item.value === menuValue);
    if (
      menuValueObject &&
      menuValueObject.activitiesCount === 0 &&
      menuValueObject.currentProgress === 0 &&
      menuValueObject.maxProgress &&
      menuValueObject.maxProgress > (menuValueObject.currentProgress || 0)
    ) {
      updateProgress({
        menu: menuValue,
        menuProgress: (menuValueObject.currentProgress || 0) + 1,
        overallProgress: currentProgress + 1,
      });
    }
  };

  const onClickNextSection = () => {
    handleUpdateProgress(currentMenu.value);
    if (nextMenuValue) {
      goToSection(nextMenuValue);
    }
  };

  const onClickPrevSection = () => {
    if (prevMenuValue) {
      handleUpdateProgress(prevMenuValue);
      goToSection(prevMenuValue);
    }
  };

  return (
    <div
      aria-hidden={collapsed}
      className={`${usingNewDesignSystem ? "hotspot_book_index_container" : ""}`}
      style={
        usingNewDesignSystem
          ? {}
          : {
              display: "flex",
              flexDirection: "row-reverse",
            }
      }
    >
      <Notebook.IndexBlock className="book_index">
        <Notebook.Header
          className={`book_index_header${usingNewDesignSystem ? " hotspot_navbar_header" : ""}`}
        >
          {usingNewDesignSystem ? (
            <ProgressBarBlock
              {...({
                className: "index-titleBlock",
                title: title == "" ? description : title,
                maxProgress: maxProgress,
                currentProgress: currentProgress,
                progress: progress,
                menuList: [],
              } as Record<string, unknown>)}
            />
          ) : (
            <>
              <Notebook.TitleBlock
                className="index-titleBlock"
                title={title}
                text={description}
                ariaLabel="Lab notebook section heading."
              />
              <Notebook.GlossaryButton />
            </>
          )}
        </Notebook.Header>
        {/* MenuList component accepts these props but TypeScript doesn't know about them */}
        <Notebook.MenuList
          {...({
            className: `book_index_chapters${usingNewDesignSystem ? " hotspot_book_index_chapters" : ""}`,
            menuList: menuList,
            onSelect: goToSection,
            activeMenuClass: "chapter-active",
            inactiveMenuClass: "chapter",
            displayProgress: displayProgress,
            ...(usingNewDesignSystem && {
              lockedMenuClass: "chapter-locked",
              enableActiveGhost: true,
            }),
          } as unknown as Record<string, unknown>)}
        />
      </Notebook.IndexBlock>
      <Notebook.ContentBlock className="book_content">
        <Notebook.ContentBody
          className={
            contentBodyClass +
            (usingNewDesignSystem ? " hotspot_book_content_body" : "")
          }
        >
          <Notebook.ContentReadArea className="read_block">
            <Notebook.ContentTitle
              className="content_title"
              title={currentMenu.title}
              number={currentMenu.number}
            />
            <Notebook.ContentComponents
              components={components}
              className="content_component"
              simSpecificComponentMap={simSpecificComponentMap}
            />
            {usingNewDesignSystem ? (
              <BackAndForthSlab
                onNextClick={onClickNextSection}
                onPreviousClick={onClickPrevSection}
                showNext={!isLastChapter}
                showPrevious={!isFirstChapter}
                nextButtonVariant={
                  currentMenu?.maxProgress === currentMenu?.currentProgress
                    ? "primary"
                    : "ghost"
                }
              />
            ) : null}
          </Notebook.ContentReadArea>
          {/* Need to add End Simulation Modal ? */}
        </Notebook.ContentBody>
        {!usingNewDesignSystem && (
          <PopoutFooter
            prevSection={onClickPrevSection}
            section={currentMenu.title}
            nextSection={onClickNextSection}
            isSectionComplete={
              currentMenu?.maxProgress === currentMenu?.currentProgress
            }
            /* PopoutFooter component accepts these props but TypeScript doesn't know about them */
            {...({
              isFirstChapter,
              isLastChapter,
            } as unknown as Record<string, unknown>)}
          />
        )}
      </Notebook.ContentBlock>
    </div>
  );
}
